import React, { useEffect, useRef } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Blocks,
  Building2,
  CheckCircle2,
  Link2,
  QrCode,
  ScanLine,
  ShieldCheck,
  Smartphone,
  UsersRound,
  WalletCards,
} from "lucide-react";
import { Link } from "react-router-dom";
import wonyaPayAppLogo from "../../assets/icon3.png";
import { useLanguage } from "../../i18n/LanguageProvider";

const HomeHero = () => {
  const { t } = useLanguage();
  const visualRef = useRef(null);
  const animationFrameRef = useRef(0);
  const visualBoundsRef = useRef(null);
  const parallaxEnabledRef = useRef(false);

  useEffect(() => {
    const reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    const compactViewportQuery = window.matchMedia("(max-width: 920px)");
    const finePointerQuery = window.matchMedia("(pointer: fine)");

    const updateParallaxAvailability = () => {
      parallaxEnabledRef.current =
        !reducedMotionQuery.matches &&
        !compactViewportQuery.matches &&
        finePointerQuery.matches;
      visualBoundsRef.current = null;
    };

    updateParallaxAvailability();
    reducedMotionQuery.addEventListener("change", updateParallaxAvailability);
    compactViewportQuery.addEventListener("change", updateParallaxAvailability);
    finePointerQuery.addEventListener("change", updateParallaxAvailability);
    window.addEventListener("resize", updateParallaxAvailability, {
      passive: true,
    });

    return () => {
      reducedMotionQuery.removeEventListener(
        "change",
        updateParallaxAvailability,
      );
      compactViewportQuery.removeEventListener(
        "change",
        updateParallaxAvailability,
      );
      finePointerQuery.removeEventListener(
        "change",
        updateParallaxAvailability,
      );
      window.removeEventListener("resize", updateParallaxAvailability);
      window.cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  const cacheVisualBounds = (event) => {
    if (parallaxEnabledRef.current && event.pointerType !== "touch") {
      visualBoundsRef.current = event.currentTarget.getBoundingClientRect();
    }
  };

  const handlePointerMove = (event) => {
    if (!parallaxEnabledRef.current || event.pointerType === "touch") {
      return;
    }

    const element = visualRef.current;
    if (!element) {
      return;
    }

    const bounds = visualBoundsRef.current ?? element.getBoundingClientRect();
    visualBoundsRef.current = bounds;
    const horizontal = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
    const vertical = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;

    window.cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = window.requestAnimationFrame(() => {
      element.style.setProperty(
        "--ws-pointer-x",
        `${(horizontal * 5).toFixed(2)}px`,
      );
      element.style.setProperty(
        "--ws-pointer-y",
        `${(vertical * 5).toFixed(2)}px`,
      );
      element.style.setProperty(
        "--ws-rotate-x",
        `${(-vertical * 1.2).toFixed(2)}deg`,
      );
      element.style.setProperty(
        "--ws-rotate-y",
        `${(horizontal * 1.2).toFixed(2)}deg`,
      );
    });
  };

  const resetPointerEffect = () => {
    const element = visualRef.current;
    if (!element) {
      return;
    }

    element.style.setProperty("--ws-pointer-x", "0px");
    element.style.setProperty("--ws-pointer-y", "0px");
    element.style.setProperty("--ws-rotate-x", "0deg");
    element.style.setProperty("--ws-rotate-y", "0deg");
    visualBoundsRef.current = null;
  };

  return (
    <section className="ws-home-hero" aria-labelledby="ws-home-title">
      <div className="ws-home-hero__pattern" aria-hidden="true">
        <span className="ws-home-hero__orb ws-home-hero__orb--one" />
        <span className="ws-home-hero__orb ws-home-hero__orb--two" />
        <span className="ws-home-hero__flow ws-home-hero__flow--one" />
        <span className="ws-home-hero__flow ws-home-hero__flow--two" />
      </div>

      <div className="ws-shell ws-home-hero__grid">
        <div className="ws-home-hero__copy">
          <div className="ws-eyebrow ws-hero-enter ws-hero-enter--badge">
            <BadgeCheck size={18} aria-hidden="true" />
            {t("Entreprise technologique congolaise")}
          </div>
          <h1 id="ws-home-title">
            <span className="ws-hero-title-line ws-hero-title-line--one">
              {t("Les paiements numériques,")}
            </span>{" "}
            <span className="ws-hero-title-line ws-hero-title-line--two">
              {t("conçus pour vos réalités")}
            </span>
          </h1>
          <p className="ws-hero-enter ws-hero-enter--text">
            {t(
              "WonyaSoft conçoit WonyaPay, BulkPay, des API de paiement et des solutions monétiques adaptées aux entreprises et institutions en RDC.",
            )}
          </p>
          <div className="ws-button-row ws-hero-enter ws-hero-enter--actions">
            <Link className="ws-button ws-button--primary" to="/solutions">
              {t("Explorer nos solutions")}{" "}
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <Link className="ws-button ws-button--secondary" to="/appmobile">
              <Smartphone size={18} aria-hidden="true" />{" "}
              {t("Découvrir l’application WonyaPay")}
            </Link>
          </div>
          <div
            className="ws-trust-row ws-hero-enter ws-hero-enter--trust"
            aria-label={t("Un cadre de confiance pour vos paiements")}
          >
            <span>
              <ShieldCheck size={18} aria-hidden="true" />{" "}
              {t("Agrément Banque Centrale du Congo")}
            </span>
            <span>
              <ScanLine size={18} aria-hidden="true" />{" "}
              {t("Paiements et agrégation Mobile Money")}
            </span>
          </div>
        </div>

        <div
          ref={visualRef}
          className="ws-home-hero__visual ws-hero-enter ws-hero-enter--visual"
          role="img"
          aria-label={t(
            "Écosystème WonyaPay : application mobile, API et paiements en masse",
          )}
          onPointerEnter={cacheVisualBounds}
          onPointerMove={handlePointerMove}
          onPointerLeave={resetPointerEffect}
        >
          <div className="ws-platform-card">
            <div className="ws-platform-card__shine" aria-hidden="true" />
            <div className="ws-platform-card__top">
              <span>WonyaSoft</span>
              <span className="ws-status">
                <i /> {t("WonyaPay actif")}
              </span>
            </div>
            <div className="ws-platform-card__phone">
              <div className="ws-platform-card__notch" />
              <div className="ws-platform-card__app-logo">
                <img
                  src={wonyaPayAppLogo}
                  alt={t("Logo de l’application WonyaPay")}
                  width="74"
                  height="74"
                />
              </div>
              <strong>{t("Application mobile WonyaPay")}</strong>
              <span>
                {t("Paiement via les services Mobile Money disponibles")}
              </span>
              <div className="ws-platform-card__qr">
                <QrCode size={45} aria-hidden="true" />
                <span aria-hidden="true" />
              </div>
            </div>
            <div className="ws-platform-card__flows" aria-hidden="true">
              <span>
                <Blocks size={18} /> API WonyaPay
              </span>
              <span>
                <UsersRound size={18} /> BulkPay
              </span>
              <span>
                <Building2 size={18} /> {t("Sur devis")}
              </span>
            </div>
          </div>

          <div
            className="ws-hero-float ws-hero-float--success"
            aria-hidden="true"
          >
            <CheckCircle2 size={18} />
            <span>{t("Transaction réussie")}</span>
          </div>
          <div className="ws-hero-float ws-hero-float--api" aria-hidden="true">
            <Link2 size={18} />
            <span>{t("API connectée")}</span>
          </div>
          <div
            className="ws-hero-float ws-hero-float--mobile"
            aria-hidden="true"
          >
            <WalletCards size={18} />
            <span>{t("Paiement Mobile Money")}</span>
          </div>
          <div className="ws-hero-float ws-hero-float--qr" aria-hidden="true">
            <QrCode size={18} />
            <span>{t("QR Code vérifié")}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
